/** Settings destinations are independent of menu visibility and translated labels.
 * Keep legacy keys as aliases: existing shortcuts and assistant commands use them.
 * Permissions describe access to a page, never a replacement for API enforcement.
 */
// @ts-expect-error — features.js is a shared JavaScript module.
import { VOUCHERS_ENABLED } from './features.js';

export const settingsPageIds = [
  'company-profile', 'company-prices', 'branding', 'people', 'roles', 'working-time', 'holidays',
  'general-connections', 'accounting-connections', 'email-sending', 'review-email',
  'whatsapp-text', 'letterhead', 'document-texts', 'document-language', 'attachments',
  'file-names', 'terms', 'number-ranges', 'payment-terms', 'payment-code',
  'partial-invoices', 'labour-share', 'reminders', 'expenses-vehicles', 'voucher-rules',
  'wage-groups', 'building-blocks', 'surcharges', 'units', 'lead-assistant',
  'telephone-assistant', 'password',
] as const;

export type SettingsPage = typeof settingsPageIds[number];
export type SettingsGroupId = 'company' | 'connections' | 'templates' | 'accounting' | 'calculation' | 'automation' | 'preferences';
export type SettingsSubgroupId = 'profile' | 'email' | 'whatsapp' | 'documents' | 'invoicing';
export type SettingsPermissionCheck = (permission: string) => boolean;
export type SettingsTranslate = (key: string) => string;

export interface SettingsPageDefinition {
  id: SettingsPage;
  legacyKey: string;
  group: SettingsGroupId;
  subgroup?: SettingsSubgroupId;
  labelKey: string;
  icon: string;
  readPermission?: string;
  writePermission?: string;
  available: boolean;
  aliases: readonly string[];
}

export interface SettingsGroupDefinition {
  id: SettingsGroupId;
  labelKey: string;
  descriptionKey: string;
  icon: string;
}

export const settingsGroups: readonly SettingsGroupDefinition[] = [
  { id: 'company', labelKey: 'uxSettings.groups.company', descriptionKey: 'uxSettings.descriptions.company', icon: 'apartment' },
  { id: 'connections', labelKey: 'uxSettings.groups.connections', descriptionKey: 'uxSettings.descriptions.connections', icon: 'hub' },
  { id: 'templates', labelKey: 'uxSettings.groups.templates', descriptionKey: 'uxSettings.descriptions.templates', icon: 'description' },
  { id: 'accounting', labelKey: 'uxSettings.groups.accounting', descriptionKey: 'uxSettings.descriptions.accounting', icon: 'receipt_long' },
  { id: 'calculation', labelKey: 'uxSettings.groups.calculation', descriptionKey: 'uxSettings.descriptions.calculation', icon: 'calculate' },
  { id: 'automation', labelKey: 'uxSettings.groups.automation', descriptionKey: 'uxSettings.descriptions.automation', icon: 'auto_awesome' },
  { id: 'preferences', labelKey: 'uxSettings.groups.preferences', descriptionKey: 'uxSettings.descriptions.preferences', icon: 'person' },
];

export const settingsSubgroups: Readonly<Record<SettingsSubgroupId, { labelKey: string; icon: string }>> = {
  profile: { labelKey: 'uxSettings.subgroups.profile', icon: 'apartment' },
  email: { labelKey: 'uxSettings.subgroups.email', icon: 'mail' },
  whatsapp: { labelKey: 'uxSettings.subgroups.whatsapp', icon: 'chat' },
  documents: { labelKey: 'uxSettings.subgroups.documents', icon: 'description' },
  invoicing: { labelKey: 'uxSettings.subgroups.invoicing', icon: 'receipt_long' },
};

function page(id: SettingsPage, legacyKey: string, group: SettingsGroupId, icon: string,
  options: Partial<Omit<SettingsPageDefinition, 'id' | 'legacyKey' | 'group' | 'icon'>> = {}): SettingsPageDefinition {
  return { id, legacyKey, group, icon, labelKey: `uxSettings.pages.${id}`, available: true, aliases: [], ...options };
}
const layout = { readPermission: 'layout:read', writePermission: 'layout:write' };
const accounting = { readPermission: 'accounting:read', writePermission: 'accounting:write', subgroup: 'invoicing' as const };

export const settingsPages: readonly SettingsPageDefinition[] = [
  page('company-profile', 'unternehmensdetails', 'company', 'apartment', { subgroup: 'profile', aliases: ['company details', 'firmenprofil'] }),
  page('company-prices', 'firmenpreise', 'company', 'table_view', { readPermission: 'documents:read', writePermission: 'org:settings', aliases: ['excel', 'price catalog', 'firmenpreise', 'ausschreibung'] }),
  page('branding', 'design', 'company', 'palette', { subgroup: 'profile', writePermission: 'org:settings', aliases: ['logo', 'theme', 'farbe'] }),
  // The current employee page also owns the signed-in user's own profile.
  page('people', 'mitarbeiter', 'company', 'group', { writePermission: 'members:write', aliases: ['employees', 'members', 'invitations', 'einladungen', 'own profile'] }),
  page('roles', 'rollen', 'company', 'shield', { readPermission: 'roles:read', writePermission: 'roles:write', aliases: ['permissions', 'zugriffsrechte'] }),
  page('working-time', 'arbeitszeiten', 'company', 'schedule', { readPermission: 'worktime:read', writePermission: 'worktime:write' }),
  page('holidays', 'feiertage', 'company', 'event', { readPermission: 'holidays:read', writePermission: 'holidays:write' }),
  page('general-connections', 'verbindungen', 'connections', 'hub', { readPermission: 'connections:read', writePermission: 'connections:write', aliases: ['gmail', 'google', 'calendar sync', 'kalender synchronisieren', 'whatsapp connection'] }),
  page('accounting-connections', 'buchhaltung-verbindungen', 'connections', 'sync_alt', { readPermission: 'costs:read', writePermission: 'costs:approve', aliases: ['bexio', 'abacus', 'export destination', 'buchhaltung'] }),
  page('email-sending', 'email-versand', 'templates', 'mail', { ...layout, subgroup: 'email', aliases: ['email sending', 'signature', 'signatur', 'subject', 'betreff'] }),
  page('review-email', 'bewertung', 'templates', 'star', { ...layout, subgroup: 'email', aliases: ['review request', 'bewertungs-mail', 'bewertungsmail'] }),
  page('whatsapp-text', 'whatsapp-text', 'templates', 'chat', { ...layout, subgroup: 'whatsapp', aliases: ['whatsapp templates', 'whatsapp text', 'begleittext'] }),
  page('letterhead', 'briefpapier', 'templates', 'description', { ...layout, subgroup: 'documents', aliases: ['margins', 'font', 'background', 'seitenränder', 'schrift'] }),
  page('document-texts', 'texte', 'templates', 'article', { ...layout, subgroup: 'documents', aliases: ['opening', 'closing', 'anfangstexte', 'schlusstexte'] }),
  page('document-language', 'dokumentsprache', 'templates', 'translate', { ...layout, subgroup: 'documents' }),
  page('attachments', 'anhaenge', 'templates', 'attach_file', { ...layout, subgroup: 'documents', aliases: ['automatic attachments', 'anhänge'] }),
  page('file-names', 'dateinamen', 'templates', 'draft', { ...layout, subgroup: 'documents', aliases: ['file naming', 'dateiname'] }),
  page('terms', 'agb', 'templates', 'gavel', { ...layout, subgroup: 'documents', aliases: ['terms and conditions', 'allgemeine geschäftsbedingungen'] }),
  page('number-ranges', 'nummernkreise', 'accounting', 'tag', { ...accounting, aliases: ['numbering', 'invoice numbers'] }),
  page('payment-terms', 'zahlungsziel', 'accounting', 'payments', { ...accounting, aliases: ['discount', 'skonto', 'zahlungsfrist'] }),
  page('payment-code', 'bezahlcode', 'accounting', 'qr_code_2', { ...accounting, aliases: ['qr', 'payment slip', 'qr-rechnung'] }),
  page('partial-invoices', 'abschlagsrechnungen', 'accounting', 'request_quote', { ...accounting, aliases: ['akonto', 'teilrechnung', 'schlussrechnung'] }),
  page('labour-share', 'lohnkostenanteil', 'accounting', 'engineering', accounting),
  page('reminders', 'mahnungen', 'accounting', 'receipt_long', { ...accounting, aliases: ['dunning', 'overdue', 'zahlungserinnerung'] }),
  page('expenses-vehicles', 'kosten-fahrzeuge', 'accounting', 'directions_car', { readPermission: 'costs:read', writePermission: 'costs:write', aliases: ['vehicle', 'mileage', 'rates', 'fahrzeuge', 'kilometer'] }),
  page('voucher-rules', 'gutscheine', 'accounting', 'redeem', { available: VOUCHERS_ENABLED, readPermission: 'vouchers:read', writePermission: 'vouchers:write', aliases: ['voucher settings', 'gutschein'] }),
  page('wage-groups', 'lohngruppen', 'calculation', 'groups', { readPermission: 'wages:read', writePermission: 'wages:write' }),
  page('building-blocks', 'bausteine', 'calculation', 'widgets', { readPermission: 'bausteine:read', writePermission: 'bausteine:write', aliases: ['reusable line items', 'positionen'] }),
  page('surcharges', 'zuschlaege', 'calculation', 'percent', { readPermission: 'surcharges:read', writePermission: 'surcharges:write', aliases: ['markups', 'zuschläge'] }),
  page('units', 'einheiten', 'calculation', 'straighten', { readPermission: 'units:read', writePermission: 'units:write' }),
  page('lead-assistant', 'lead-agent', 'automation', 'person_search', { readPermission: 'leads:read', writePermission: 'leads:write', aliases: ['lead agent'] }),
  page('telephone-assistant', 'telefonassistent', 'automation', 'call', { available: false, aliases: ['phone assistant'] }),
  page('password', 'passwort', 'preferences', 'password', { aliases: ['security', 'sicherheit', 'account'] }),
];

const normalize = (value: string) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[\s_-]+/g, ' ').trim();

/** Exact IDs/legacy keys only. Search aliases must not make routing ambiguous. */
export function resolveSettingsPage(value: unknown): SettingsPage | null {
  if (typeof value !== 'string') return null;
  if (value === 'my-profile') return 'people';
  return settingsPages.find(item => item.id === value || item.legacyKey === value)?.id ?? null;
}

export function getSettingsPage(value: unknown): SettingsPageDefinition | undefined {
  const id = resolveSettingsPage(value);
  return settingsPages.find(item => item.id === id);
}

export function settingsPageAllowed(value: unknown, can: SettingsPermissionCheck): boolean {
  const item = getSettingsPage(value);
  return !!item && item.available && (!item.readPermission || can(item.readPermission));
}

export function visibleSettingsPages(can: SettingsPermissionCheck, group?: SettingsGroupId): SettingsPageDefinition[] {
  return settingsPages.filter(item => (!group || item.group === group) && settingsPageAllowed(item.id, can));
}

export function visibleSettingsGroups(can: SettingsPermissionCheck): SettingsGroupDefinition[] {
  return settingsGroups.filter(group => visibleSettingsPages(can, group.id).length > 0);
}

export function settingsBreadcrumbs(value: unknown): Array<{ id: string; labelKey: string }> {
  const item = getSettingsPage(value);
  if (!item) return [];
  const group = settingsGroups.find(entry => entry.id === item.group)!;
  return [group, ...(item.subgroup ? [{ id: item.subgroup, ...settingsSubgroups[item.subgroup] }] : []), item];
}

/** Search only this metadata. Never index credentials, form values, or records. */
export function searchSettings(query: string, can: SettingsPermissionCheck, translate: SettingsTranslate): SettingsPageDefinition[] {
  const terms = normalize(query).split(' ').filter(Boolean);
  if (!terms.length) return [];
  return visibleSettingsPages(can).filter(item => {
    const text = normalize([item.id, item.legacyKey, ...item.aliases,
      ...settingsBreadcrumbs(item.id).map(part => translate(part.labelKey))].join(' '));
    return terms.every(term => text.includes(term));
  });
}
